import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { isValid } from 'postcode';
import get from 'lodash/get';
import iconSrc from '../../utils/iconSrc';
import { sendEvent } from '../../utils/analytics';
import RichText from '../rich-text';
import { useApi } from '../../utils/useApi';
import { VisuallyHidden } from '../styled/accessibility';
import { trimPostcode, isPostcode } from '../form/submit';
import { consistentString } from '../../utils/content-formatting';
import { getStorageValue } from '../../utils/session-storage';
import {
  Container,
  ContentSemi,
  ContentWithSideBar,
} from '../styled/containers';
import {
  GeoLocation,
  IntroWrapper,
  SearchForm,
  SearchInput,
  SearchButton,
  SearchWrapper,
  SearchFinderWrapper,
  SearchShopTitle,
  SpinnerSVG,
  StatusMessage,
  LinkButton,
  ShopListWrapper,
} from './styles';
import Accordion from '../accordion';
import Map from '../map/index';

import { useLayout } from '../layout/context';
import ShopListing from './shop-listing';

import { checkGeoLocation, getClosestShopFromGeo } from './helpers';

const ENDPOINT = 'https://maps.googleapis.com/maps/api/geocode/json';

const Finder = ({ titleText, introText, type, shops }) => {
  const { insideContainer } = useLayout();

  const initialLocations = shops
    .map((region) => region.data.map(({ shop }) => shop))
    .flat();

  const filterMapByAccordions = () => {
    const filteredShops = shops
      .filter((item) => {
        const accId = consistentString(item.region).slice(0, 30);
        return getStorageValue('open', `accordion-${accId}`);
      })
      .map((region) => region.data.map(({ shop }) => shop))
      .flat();

    // If no accordions are open then display all shops
    return filteredShops.length > 0 ? filteredShops : initialLocations;
  };

  const [locations, setLocations] = useState(filterMapByAccordions());
  const [geoList, setGeoList] = useState(null);
  const [query, setQuery] = useState('');
  const [isQueryEmpty, setIsQueryEmpty] = useState(false);
  const [ariaLabelText, setAriaLabelText] = useState(
    'Please enter your postcode or town'
  );
  const { response, isLoading, isError, search } = useApi();

  const processRequest = async () => {
    if (!query) {
      setIsQueryEmpty(true);
      return;
    }
    await search({
      method: 'get',
      url: `${ENDPOINT}?key=${process.env.GOOGLE_MAPS_API_KEY}&address=${query}, united kingdom`,
    });
    sendEvent({
      event: 'formSubmitted',
      formName: 'shops-finder',
      query: isPostcode(query) ? trimPostcode(query) : query,
    });
  };

  useEffect(() => {
    if (isError) return;
    if (response && response.status === 'OK') {
      const { lat, lng } = get(response, 'results.0.geometry.location', {
        lat: 0,
        lng: 0,
      });

      if (lat !== 0 && lng !== 0) {
        getClosestShopFromGeo({ lat, lng }, setGeoList, setLocations, shops);
      }
    }
  }, [response, isError, shops]);

  return (
    <Container $padding={!insideContainer}>
      <IntroWrapper>
        {introText && <RichText richText={introText} />}
      </IntroWrapper>
      <ContentWithSideBar>
        <ContentSemi>
          <SearchFinderWrapper>
            <SearchShopTitle>Find a shop near you</SearchShopTitle>
            <SearchForm
              role="search"
              isLoading={!isLoading && !isError}
              data-testid="search-form"
              onSubmit={(event) => {
                event.preventDefault();
                processRequest();
              }}
            >
              <label htmlFor="search-input">Your postcode or town</label>
              <SearchWrapper>
                <SearchInput
                  type="text"
                  data-testid="search-input"
                  id="search-input"
                  name="search-input"
                  value={query}
                  aria-label={ariaLabelText}
                  onChange={(event) => {
                    const inputValue = event.target.value;
                    if (!query && inputValue) setIsQueryEmpty(false);
                    setQuery(inputValue);
                    setAriaLabelText(
                      isValid(inputValue) // check if it's a valid postcode, then add spaces between them so that screen reader pronounces one at a time
                        ? inputValue
                            .replace(/\s/g, '')
                            .replace(/(\S)/g, '$1 ')
                            .trim()
                        : inputValue
                    );
                  }}
                />
                <SearchButton
                  bg="secondary-standard"
                  type="submit"
                  disabled={isLoading}
                >
                  <VisuallyHidden>Submit Search</VisuallyHidden>
                  Find
                </SearchButton>
              </SearchWrapper>

              <StatusMessage aria-live="polite">
                {isQueryEmpty && <p>Please insert a postcode or a town</p>}
                {isError && (
                  <p>
                    Sorry, we couldn't find any results based on your search,
                    please try again
                  </p>
                )}
                {isLoading && (
                  <SpinnerSVG src={iconSrc('refresh')} cacheRequests />
                )}
              </StatusMessage>
            </SearchForm>
            <GeoLocation>
              <LinkButton
                type="submit"
                onClick={() =>
                  checkGeoLocation(setGeoList, setLocations, shops)
                }
              >
                Use my current location
              </LinkButton>
            </GeoLocation>
            <div aria-live="polite">
              {geoList &&
                geoList.length &&
                geoList.map((s, i) => (
                  <ShopListing
                    key={`${i}${s.shop.shopName.slice(0, 5)}`}
                    last={i === 4}
                    navigation={s.navigation}
                    name={s.shop.shopName}
                    contactNumber={s.shop.contactNumber}
                    address={s.shop.shortAddress}
                    distance={s.distance}
                    responseTapClass={s.shop.responseTapClass}
                  />
                ))}
            </div>
          </SearchFinderWrapper>
          {titleText ? <h2>{titleText}</h2> : null}
          <ShopListWrapper>
            {shops
              .sort((a, b) => a.region.localeCompare(b.region))
              .map((item, i) => (
                <Accordion
                  handler={() => {
                    setGeoList(null);
                    setLocations(filterMapByAccordions());
                  }}
                  key={`${i}${item.region}`}
                  id={item.region}
                  order={i + 1}
                  header={item.region}
                >
                  {item.data
                    .sort((a, b) =>
                      a.shop.shopName.localeCompare(b.shop.shopName)
                    )
                    .map((s, iter) => (
                      <ShopListing
                        key={`${i}${item.region.slice(
                          0,
                          4
                        )}${iter}${s.shop.shopName.slice(0, 4)}`}
                        last={iter === item.data.length - 1}
                        navigation={s.navigation}
                        name={s.shop.shopName}
                        address={s.shop.shortAddress}
                        contactNumber={s.shop.contactNumber}
                        responseTapClass={s.shop.responseTapClass}
                      />
                    ))}
                </Accordion>
              ))}
          </ShopListWrapper>
        </ContentSemi>
        <ContentSemi>
          <Map locations={locations} height="700px" centered zoomed />
        </ContentSemi>
      </ContentWithSideBar>
    </Container>
  );
};

Finder.propTypes = {
  titleText: PropTypes.string,
  introText: PropTypes.object,
  type: PropTypes.oneOf(['services', 'shops']),
  shops: PropTypes.array,
};

export default Finder;
